import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import Head from "next/head";

import { QuoteLandingContainer } from "@/containers/QuoteLandingContainer";

import parlors from "@/constants/parlors_detail.json";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";
import { buildParlorPageProps } from "@/utils/buildParlorPageProps";

/**
 * 네이버 검색광고 비즈채널용 견적 LP
 * - URL: parlor.goifuneral.co.kr/place/{id}/quote
 * - 광고로만 노출 (자연 검색 cannibalize 방지: noindex)
 * - CTA → www.goifuneral.co.kr/match/parlor-requests/?parlor_id={id}&{utm 보존}
 *
 * getStaticPaths: 비용 데이터(expected_charge_sum.가족장/일반장)가 유효한 장례식장만 prerender.
 * 데이터 없는 곳은 광고 노출 대상이 아니므로 경로 자체 생성 안 함 → 404.
 */

const isCostAvailable = (
  expected: { [type: string]: number } | undefined
): boolean => {
  if (!expected) return false;
  const family = expected["가족장"];
  const general = expected["일반장"];
  return (
    typeof family === "number" &&
    typeof general === "number" &&
    family > 0 &&
    general > 0
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult =
    skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) return skipBuildResult;

  const paths = parlors
    .filter(
      (parlor) =>
        parlor.is_excluded !== true &&
        isCostAvailable(parlor.expected_charge_sum)
    )
    .map((parlor) => ({
      params: { id: parlor.id.toString() },
    }));

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const parlorId = parseInt(params?.id as string);
  const { parlor, sidoName, sigunguName, companyName, managedType, jsonLd } =
    buildParlorPageProps(parlorId);

  if (!parlor || !isCostAvailable(parlor.expected_charge_sum)) {
    return { notFound: true };
  }

  // expected_charge_sum: 원 단위 → 만원 단위 변환
  // 가족장 = costMin (한국 평균 장례), 일반장 = costMax (상한)
  const familyWon = parlor.expected_charge_sum["가족장"];
  const generalWon = parlor.expected_charge_sum["일반장"];
  const mubinsoWon = parlor.expected_charge_sum["무빈소"];
  const rawMin = Math.round(familyWon / 10000);
  const rawMax = Math.round(generalWon / 10000);
  const costMubinso =
    typeof mubinsoWon === "number" && mubinsoWon > 0
      ? Math.round(mubinsoWon / 10000)
      : rawMin;
  // 데이터 오류 보정 (min > max 케이스)
  const costMin = Math.min(rawMin, rawMax);
  const costMax = Math.max(rawMin, rawMax);

  const region = [sidoName, sigunguName].filter(Boolean).join(" · ");
  const title = `${companyName} 상세비용 알아보기 - 고이장례식장`;
  const description = `${companyName} 장례비용 ${costMin.toLocaleString()}~${costMax.toLocaleString()}만원. 빈소·식대·장례용품 비용 안내, 무료견적 확인 고이 장례식장 정보에서 확인해보세요.`;
  const canonical = `https://parlor.goifuneral.co.kr/place/${parlorId}/quote/`;

  return {
    props: {
      parlorId,
      parlorName: companyName,
      region,
      managedType,
      address: parlor.address,
      sidoCode: parlor.sido ?? "",
      sigunguCode: parlor.sigungu ?? "",
      costMubinso,
      costMin,
      costMax,
      pageTitle: title,
      pageDescription: description,
      pageOgTitle: title,
      pageOgDescription: description,
      pageCanonical: canonical,
      pageJsonLd: JSON.stringify(jsonLd),
    },
  };
};

interface ParlorQuotePageProps {
  parlorId: number;
  parlorName: string;
  region: string;
  managedType: string;
  address: string;
  sidoCode: string;
  sigunguCode: string;
  costMubinso: number;
  costMin: number;
  costMax: number;
  pageTitle: string;
  pageDescription: string;
  pageCanonical: string;
  pageJsonLd?: string;
}

const ParlorQuotePage = ({
  parlorId,
  parlorName,
  region,
  managedType,
  address,
  sidoCode,
  sigunguCode,
  costMubinso,
  costMin,
  costMax,
  pageTitle,
  pageDescription,
  pageCanonical,
  pageJsonLd,
}: ParlorQuotePageProps) => {
  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageCanonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageCanonical} />
        <meta property="og:type" content="website" />
        {pageJsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageJsonLd }}
          />
        )}
      </Head>
      <QuoteLandingContainer
        parlorId={parlorId}
        parlorName={parlorName}
        region={region}
        managedType={managedType}
        address={address}
        sidoCode={sidoCode}
        sigunguCode={sigunguCode}
        costMubinso={costMubinso}
        costMin={costMin}
        costMax={costMax}
      />
    </>
  );
};

export default ParlorQuotePage;
